import { Option } from "effect";
import * as AsyncData from "foldkit/asyncData";
import type { Html, HtmlBuilder } from "foldkit/html";
import { defineView } from "foldkit/submodel";

import type { Paused, Tracked, Week } from "../../api/schemas/keepInTouch";
import { Cadence } from "../../api/schemas/records";
import { Route, linkHref } from "../../app/routes";
import { i18next } from "../../i18n";
import * as Icons from "../../widgets/icons";
import * as Cards from "../../widgets/keepInTouchCards";
import * as Picker from "../../widgets/recordPicker";
import type { Shared } from "../shared";
import { Message } from "./message";
import type { Model } from "./model";
import { swapTargetOf } from "./update";

const toCadence = (value: string): Cadence | null =>
	Cadence.literals.find((c) => c === value) ?? null;

const recordLink = (
	h: HtmlBuilder<Message>,
	shared: Shared,
	id: string,
	name: string,
): Html =>
	h.a(
		[
			h.Href(
				linkHref(
					shared.route,
					Route.Record({ id, asOf: Option.none(), via: Option.none() }),
				),
			),
			h.Class("link link-hover font-medium"),
		],
		[name],
	);

const trackedRow = (
	h: HtmlBuilder<Message>,
	shared: Shared,
	row: Tracked,
): Html =>
	h.tr(
		[h.Key(row.id)],
		[
			h.td([], [recordLink(h, shared, row.id, row.name)]),
			h.td(
				[],
				[
					h.select(
						[
							h.Class("select select-bordered select-sm"),
							h.AriaLabel(
								i18next.t("keepInTouch.page.cadenceLabel", { name: row.name }),
							),
							h.Value(row.cadence),
							h.OnChange((value) =>
								Message.ChangedCadence({
									recordId: row.id,
									cadence: toCadence(value),
								}),
							),
						],
						[
							...Cadence.literals.map((cadence) =>
								h.option(
									[h.Value(cadence), h.Selected(row.cadence === cadence)],
									[Cards.cadenceLabel(cadence)],
								),
							),
							h.option(
								[h.Value("")],
								[i18next.t("keepInTouch.page.clearCadence")],
							),
						],
					),
				],
			),
			h.td([h.Class("tabular-nums")], [row.nextDue ?? "—"]),
			h.td(
				[h.Class("text-right")],
				[
					h.button(
						[
							h.Type("button"),
							h.Class("btn btn-xs btn-ghost"),
							h.AriaLabel(
								i18next.t("keepInTouch.page.pauseLabel", { name: row.name }),
							),
							h.OnClick(
								Message.ClickedPause({
									recordId: row.id,
									cadence: row.cadence,
								}),
							),
						],
						[i18next.t("keepInTouch.page.pause")],
					),
				],
			),
		],
	);

const pausedRow = (
	h: HtmlBuilder<Message>,
	shared: Shared,
	row: Paused,
): Html =>
	h.tr(
		[h.Key(row.id)],
		[
			h.td([], [recordLink(h, shared, row.id, row.name)]),
			h.td([], [row.cadence == null ? "—" : Cards.cadenceLabel(row.cadence)]),
			h.td(
				[h.Class("text-right")],
				[
					h.button(
						[
							h.Type("button"),
							h.Class("btn btn-xs"),
							h.AriaLabel(
								i18next.t("keepInTouch.page.unpauseLabel", { name: row.name }),
							),
							h.OnClick(
								Message.ClickedUnpause({
									recordId: row.id,
									cadence: row.cadence ?? null,
								}),
							),
						],
						[i18next.t("keepInTouch.page.unpause")],
					),
				],
			),
		],
	);

const table = (
	h: HtmlBuilder<Message>,
	title: string,
	emptyText: string,
	headers: ReadonlyArray<string>,
	rows: ReadonlyArray<Html>,
): Html =>
	h.section(
		[h.Class("flex flex-col gap-2")],
		[
			h.h2([h.Class("text-lg font-semibold")], [title]),
			rows.length === 0
				? h.p([h.Class("text-sm text-base-content/70")], [emptyText])
				: h.div(
						[h.Class("overflow-x-auto")],
						[
							h.table(
								[h.Class("table table-sm")],
								[
									h.thead(
										[],
										[
											h.tr(
												[],
												headers.map((header) => h.th([], [header])),
											),
										],
									),
									h.tbody([], rows),
								],
							),
						],
					),
		],
	);

/** R12 swap-with-chosen-record: search, pick, then a separate confirm so a stray click cannot swap. */
const picker = (h: HtmlBuilder<Message>, model: Model, week: Week): Html => {
	const cards = Cards.visible(week);
	const target = Option.getOrUndefined(swapTargetOf(model));
	const chosen = Option.getOrUndefined(model.picker.chosen);
	return h.section(
		[h.Class("card card-border bg-base-100 p-4 flex flex-col gap-3")],
		[
			h.h2(
				[h.Class("text-base font-semibold")],
				[i18next.t("keepInTouch.page.pickerTitle")],
			),
			h.div(
				[h.Class("flex flex-wrap items-end gap-2")],
				[
					h.select(
						[
							h.Class("select select-bordered select-sm"),
							h.AriaLabel(i18next.t("keepInTouch.page.replaceLabel")),
							h.Value(target ?? ""),
							h.OnChange((value) =>
								Message.ChangedSwapTarget({ recordId: value }),
							),
						],
						cards.map((card) =>
							h.option(
								[
									h.Value(card.record.id),
									h.Selected(card.record.id === target),
								],
								[card.record.name],
							),
						),
					),
					h.submodel({
						slotId: "record-picker",
						view: Picker.view,
						model: model.picker,
						toParentMessage: (message: Picker.Message) =>
							Message.GotPickerMessage({ message }),
						viewInputs: {},
					}),
				],
			),
			h.div(
				[],
				[
					h.button(
						[
							h.Type("button"),
							h.Class("btn btn-sm btn-primary"),
							h.Disabled(chosen === undefined || target === undefined),
							h.AriaLabel(
								chosen === undefined
									? i18next.t("keepInTouch.page.swapInNone")
									: i18next.t("keepInTouch.page.swapIn", {
											name: chosen.name,
										}),
							),
							h.OnClick(Message.ClickedSwapIn()),
						],
						[
							chosen === undefined
								? i18next.t("keepInTouch.page.swapInNone")
								: i18next.t("keepInTouch.page.swapIn", { name: chosen.name }),
						],
					),
				],
			),
		],
	);
};

const content = (
	h: HtmlBuilder<Message>,
	model: Model,
	shared: Shared,
	week: Week,
): ReadonlyArray<Html> => {
	const cards = Cards.visible(week);
	const tracked = week.tracked.filter((row) => !row.paused);
	// R20: no candidates and nothing tracked means the circle has nothing to propose from.
	const nothingToPropose = week.cards.length === 0 && tracked.length === 0;
	const done = week.cards.length - cards.length;
	const lists = [
		table(
			h,
			i18next.t("keepInTouch.page.tracked"),
			i18next.t("keepInTouch.page.trackedEmpty"),
			[
				i18next.t("keepInTouch.page.name"),
				i18next.t("keepInTouch.page.cadence"),
				i18next.t("keepInTouch.page.nextDue"),
				"",
			],
			tracked.map((row) => trackedRow(h, shared, row)),
		),
		table(
			h,
			i18next.t("keepInTouch.page.paused"),
			i18next.t("keepInTouch.page.pausedEmpty"),
			[
				i18next.t("keepInTouch.page.name"),
				i18next.t("keepInTouch.page.cadence"),
				"",
			],
			week.paused.map((row) => pausedRow(h, shared, row)),
		),
	];
	if (nothingToPropose) {
		return [
			Cards.emptyState(h, Message.ClickedAddRecord()),
			...(week.paused.length === 0 ? [] : lists),
		];
	}
	return [
		week.cards.length === 0
			? h.empty
			: h.p(
					[h.Class("text-sm text-base-content/70")],
					[
						i18next.t("keepInTouch.page.done", {
							done,
							total: week.cards.length,
						}),
					],
				),
		cards.length === 0
			? h.p(
					[h.Class("text-sm text-base-content/70")],
					[i18next.t("keepInTouch.page.allClear")],
				)
			: Cards.view(h, {
					cards,
					current: shared.route,
					layout: "grid",
					toParentMessage: (message) => Message.GotCardMessage({ message }),
				}),
		cards.length === 0 ? h.empty : picker(h, model, week),
		...lists,
	];
};

export const view = defineView<Model, Message, Shared>((model, shared, h) => {
	const week = AsyncData.getOrElse(model.week, () => null);
	return h.div(
		[
			h.DataAttribute("testid", "page-KeepInTouch"),
			h.Class("mx-auto flex max-w-5xl flex-col gap-6"),
		],
		[
			h.header(
				[],
				[
					h.h1(
						[h.Class("text-3xl font-bold")],
						[i18next.t("keepInTouch.page.title")],
					),
					h.p(
						[h.Class("mt-1 text-base-content/70")],
						[i18next.t("keepInTouch.page.description")],
					),
				],
			),
			Option.match(model.error, {
				onNone: () => h.empty,
				onSome: (error) =>
					h.p([h.Role("alert"), h.Class("text-sm text-error")], [error]),
			}),
			...(week === null
				? [
						AsyncData.isFailure(model.week)
							? h.div(
									[h.Role("alert"), h.Class("alert alert-error")],
									[h.span([], [i18next.t("keepInTouch.error.load")])],
								)
							: h.div(
									[
										h.Role("status"),
										h.Class(
											"flex items-center gap-2 py-8 text-base-content/70",
										),
									],
									[
										Icons.Loader2(h, { size: 20, class: "animate-spin" }),
										h.span([], [i18next.t("keepInTouch.page.loading")]),
									],
								),
					]
				: content(h, model, shared, week)),
		],
	);
});
